/**
 * A row of pill radio buttons.
 *
 * **Eight of the Next.js generate controls were the same forty lines.** `LightingSelector`,
 * `WeatherSelector`, `PortraitFramingSelector`, `GenerationTierSelector`,
 * `VariationDirectionSelector`, `VariationsSelector`, `ModeToggle` and the print-size row
 * differed only in their option list and their `aria-label`; each restated the identical
 * `role="radiogroup"`, `aria-checked`, focus-ring and disabled styling. One component with an
 * options prop is the whole of that duplication removed, and it is why this port is a
 * fraction of the size of what it replaces.
 *
 * `ToggleButtonGroup` from MUI would be the obvious component, but it is not a radio group to
 * a screen reader — it renders `aria-pressed` buttons, where these are a single choice from a
 * set and announce correctly as `radio`s inside a `radiogroup`. That distinction is worth more
 * here than the styling MUI would bring, so the roles are explicit and MUI supplies only the
 * button itself.
 *
 * ## The hint, and why it is now visible
 *
 * The hint used to travel as `title` alone, "exactly as the originals did", on the reasoning
 * that the page already carries a description per section and seven hints under seven pills
 * would bury the controls they explain. The second half of that is right; the first half does
 * not hold. `title` is a desktop-hover affordance and nothing else: no touch device shows it,
 * no keyboard user can reach it, and screen-reader support for it is inconsistent. Options
 * like "Subject in setting", "Symbolic concept" and "Refine one direction" are not
 * self-explanatory, so for most users the explanation simply was not there.
 *
 * Showing **only the selected option's** hint answers both: one line, not seven, and the
 * explanation is readable by everyone. `title` stays on the others, so hovering an option you
 * have not chosen still previews what it would do.
 */

import { Button, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { useRovingRadioGroup } from "@/components/generate/useRovingRadioGroup";

export interface PillOption<T> {
  value: T;
  label: ReactNode;
  hint?: string;
}

export interface PillGroupProps<T> {
  /** Names the group for a screen reader — there are a dozen of these on the page. */
  label: string;
  options: ReadonlyArray<PillOption<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}

export function PillGroup<T extends string | number>({
  label,
  options,
  value,
  onChange,
  disabled = false,
}: PillGroupProps<T>) {
  const { groupProps, getRadioProps } = useRovingRadioGroup(
    options.map((option) => option.value),
    value,
    onChange,
    disabled,
  );
  const selectedHint = options.find((option) => option.value === value)?.hint;

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        role="radiogroup"
        aria-label={label}
        sx={{ flexWrap: "wrap" }}
        {...groupProps}
      >
        {options.map((option, index) => {
          const active = option.value === value;
          return (
            <Button
              key={String(option.value)}
              role="radio"
              aria-checked={active}
              title={option.hint}
              disabled={disabled}
              size="small"
              variant={active ? "contained" : "outlined"}
              onClick={() => onChange(option.value)}
              {...getRadioProps(index)}
            >
              {option.label}
            </Button>
          );
        })}
      </Stack>
      {/*
        `aria-live="polite"`: arrowing through the group changes the selection, and this line
        is the explanation of what was just selected — it has to be announced, not merely
        repainted. Rendered only when the selected option has a hint, so groups whose labels
        speak for themselves (the variation counts, the print sizes) gain no empty row.
      */}
      {selectedHint ? (
        <Typography variant="caption" color="text.secondary" aria-live="polite">
          {selectedHint}
        </Typography>
      ) : null}
    </Stack>
  );
}
